import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import io from 'socket.io-client';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { API_URL, SOCKET_URL } from './config';
import './QRCodePage.css';

const socket = io(SOCKET_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function QRCodePage() {
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState('');
  const [status, setStatus] = useState({ status: 'initializing', message: 'Initializing...' });
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [accounts, setAccounts] = useState([]); // List of all logged in accounts
  const [currentAccountId, setCurrentAccountId] = useState(null); // Currently showing QR for this account
  const [isAddingNewAccount, setIsAddingNewAccount] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDisconnectWhatsApp = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp? You will need to scan QR code again to reconnect.')) {
      return;
    }

    setIsDisconnecting(true);
    try {
      const response = await api.post('/api/disconnect-whatsapp');
      if (response.data.success) {
        setIsLoggedOut(true);
        setQrCode('');
        setStatus({ status: 'disconnected', message: 'WhatsApp disconnected successfully' });
      }
    } catch (error) {
      console.error('Error disconnecting WhatsApp:', error);
      alert(error.response?.data?.error || 'Failed to disconnect WhatsApp');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRequestNewQR = async () => {
    try {
      setStatus({ status: 'initializing', message: 'Generating new QR code...' });
      setQrCode('');
      setIsLoggedOut(false);
      
      // Request new QR code from server
      socket.emit('request-qr');
    } catch (error) {
      console.error('Error requesting new QR:', error);
      setStatus({ status: 'error', message: 'Failed to generate QR code' });
    }
  };

  // Fetch all logged in accounts
  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/accounts');
      if (response.data.success) {
        setAccounts(response.data.accounts || []);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleAddNewNumber = async () => {
    setIsAddingNewAccount(true);
    setQrCode('');
    setStatus({ status: 'initializing', message: 'Generating QR code for new account...' });
    
    try {
      const response = await api.post('/api/add-new-account');
      if (response.data.success) {
        const newAccountId = response.data.accountId;
        setCurrentAccountId(newAccountId);
        console.log('New account QR generation initiated:', newAccountId);
        console.log('Waiting for QR code for account:', newAccountId);
        // Keep isAddingNewAccount true until QR code is received or account is ready
      }
    } catch (error) {
      console.error('Error adding new account:', error);
      setStatus({ status: 'error', message: 'Failed to generate QR code for new account' });
      setIsAddingNewAccount(false);
      setCurrentAccountId(null);
    }
  };

  const handleSetActive = async (accountId) => {
    try {
      const response = await api.post('/api/set-active-account', { accountId });
      if (response.data.success) {
        fetchAccounts(); // Refresh accounts list
      }
    } catch (error) {
      console.error('Error setting active account:', error);
      alert(error.response?.data?.error || 'Failed to set account as active');
    }
  };

  const handleLogoutAccount = async (accountId) => {
    if (!window.confirm(`Are you sure you want to logout account ${accountId}? This will disconnect the WhatsApp session.`)) {
      return;
    }

    try {
      const response = await api.post('/api/logout-account', { accountId });
      if (response.data.success) {
        fetchAccounts(); // Refresh accounts list
        
        // If shouldShowQR is true, it means no accounts remain and we need to show QR for new account
        if (response.data.shouldShowQR) {
          console.log('No accounts remaining, generating QR for new account...');
          setStatus({ status: 'initializing', message: 'Generating QR code for new account...' });
          setQrCode('');
          setIsAddingNewAccount(true);
          
          // Generate new account QR
          try {
            const newAccountResponse = await api.post('/api/add-new-account');
            if (newAccountResponse.data.success) {
              const newAccountId = newAccountResponse.data.accountId;
              setCurrentAccountId(newAccountId);
              console.log('New account QR generation initiated:', newAccountId);
            }
          } catch (error) {
            console.error('Error generating new account QR:', error);
            setStatus({ status: 'error', message: 'Failed to generate QR code for new account' });
            setIsAddingNewAccount(false);
            setCurrentAccountId(null);
          }
        } else if (accountId === 'default') {
          // Default account logged out but other accounts exist
          setIsLoggedOut(false);
          setQrCode('');
          setStatus({ status: 'ready', message: 'Other accounts are still connected' });
        }
      }
    } catch (error) {
      console.error('Error logging out account:', error);
      alert(error.response?.data?.error || 'Failed to logout account');
    }
  };

  useEffect(() => {
    // Fetch accounts on mount
    fetchAccounts();
    const accountsInterval = setInterval(fetchAccounts, 3000); // Refresh every 3 seconds

    // Listen for QR code (for default account and new accounts)
    socket.on('qr', (qrData) => {
      console.log('QR code received', qrData);
      // Handle both old format (string) and new format (object with accountId)
      if (typeof qrData === 'string') {
        // Default account QR - only show if we're not adding a new account
        if (!currentAccountId && !isAddingNewAccount) {
          setQrCode(qrData);
          setStatus({ status: 'scan', message: 'Please scan the QR code with your WhatsApp' });
        }
      } else if (qrData.qr && qrData.accountId) {
        // New account QR - show it if it matches current account or if we're adding new account
        if (qrData.accountId === currentAccountId || isAddingNewAccount) {
          setQrCode(qrData.qr);
          setStatus({ status: 'scan', message: `Please scan the QR code for new account` });
          console.log('Setting QR code for account:', qrData.accountId);
          // Make sure currentAccountId is set
          if (!currentAccountId) {
            setCurrentAccountId(qrData.accountId);
          }
        }
      }
    });

    // Listen for status updates
    socket.on('status', (statusData) => {
      console.log('Status received:', statusData, 'isAddingNewAccount:', isAddingNewAccount, 'currentAccountId:', currentAccountId);
      // Only update if not for a specific account or if it's the default account
      // Don't update status if we're waiting for a new account QR code
      if ((!statusData.accountId || statusData.accountId === 'default') && !isAddingNewAccount) {
        setStatus(statusData);
        if (statusData.status === 'ready') {
          console.log('WhatsApp is ready! Showing success message');
          setQrCode(''); // Clear QR code when ready
          setIsLoggedOut(false); // Reset logged out state
          fetchAccounts(); // Refresh accounts
        } else if (statusData.status === 'disconnected') {
          setIsLoggedOut(true);
          setQrCode('');
        }
      } else if (statusData.accountId && statusData.accountId === currentAccountId) {
        // Handle status for current new account being added
        console.log('Status update for new account:', statusData.accountId, statusData.status);
        if (statusData.status === 'ready') {
          setQrCode(''); // Clear QR code when ready
          setIsAddingNewAccount(false);
          setCurrentAccountId(null);
          fetchAccounts(); // Refresh accounts
        } else if (statusData.status === 'scan') {
          // Don't clear QR code, just update status
          setStatus({ status: 'scan', message: statusData.message || 'Please scan the QR code' });
        }
      }
    });

    // Listen for ready event specifically (for default account)
    socket.on('whatsapp-ready', (data) => {
      console.log('WhatsApp ready event received:', data);
      // Only handle if we're not adding a new account
      if (!isAddingNewAccount) {
        setStatus({ status: 'ready', message: data.message || 'WhatsApp is connected and ready!' });
        setQrCode(''); // Clear QR code
        setIsLoggedOut(false); // Reset logged out state
        setCurrentAccountId(null);
        fetchAccounts(); // Refresh accounts list
      }
    });

    // Listen for new account ready
    socket.on('account-ready', (data) => {
      console.log('Account ready event received:', data);
      if (data.accountId === currentAccountId || isAddingNewAccount) {
        setStatus({ status: 'ready', message: `Account ${data.phoneNumber || data.accountId} connected successfully!` });
        setQrCode('');
        setIsAddingNewAccount(false);
        setCurrentAccountId(null);
        fetchAccounts(); // Refresh accounts list
      }
    });

    // Listen for account authenticated
    socket.on('account-authenticated', (data) => {
      console.log('Account authenticated:', data);
    });

    // Listen for account disconnected
    socket.on('account-disconnected', (data) => {
      console.log('Account disconnected:', data);
      fetchAccounts(); // Refresh accounts list
    });

    // Listen for account active changed
    socket.on('account-active-changed', (data) => {
      console.log('Account active changed:', data);
      fetchAccounts(); // Refresh accounts list
    });

    // Listen for connection
    socket.on('connect', () => {
      console.log('Socket connected');
      // Request QR code if not ready
      if (!qrCode) {
        setTimeout(() => {
          checkStatus();
        }, 1000);
      }
    });

    // Listen for connection errors
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setStatus({ status: 'error', message: 'Unable to connect to server' });
    });

    // Check initial status
    checkStatus();

    // Poll status every 2 seconds to catch ready status even if socket event misses
    const statusInterval = setInterval(() => {
      checkStatus();
    }, 2000);

    return () => {
      socket.off('qr');
      socket.off('status');
      socket.off('whatsapp-ready');
      socket.off('account-ready');
      socket.off('account-authenticated');
      socket.off('account-disconnected');
      socket.off('account-active-changed');
      socket.off('connect');
      socket.off('connect_error');
      clearInterval(statusInterval);
      clearInterval(accountsInterval);
    };
  }, [currentAccountId, isAddingNewAccount]);

  const checkStatus = async () => {
    try {
      const res = await api.get('/api/status');
      if (res.data.ready) {
        // Only clear QR code if we're not adding a new account
        if (!isAddingNewAccount && !currentAccountId) {
          console.log('Status check: WhatsApp is ready!');
          setStatus({ status: 'ready', message: 'WhatsApp is connected and ready!' });
          setQrCode(''); // Clear QR code
        }
      }
      // Don't update status if already scanning or if we have QR code for new account
    } catch (error) {
      console.error('Error checking status:', error);
      // Don't update to error if we're already scanning
    }
  };

  const handleRegenerateQR = async () => {
    try {
      setStatus({ status: 'initializing', message: 'Regenerating QR code...' });
      setQrCode('');
      await api.post('/api/regenerate-qr');
      // Socket will receive new QR code via event
    } catch (error) {
      console.error('Error regenerating QR:', error);
      setStatus({ status: 'error', message: 'Failed to regenerate QR code' });
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'ready':
        return '#25D366'; // WhatsApp green
      case 'scan':
        return '#FFC107'; // Yellow
      case 'error':
      case 'auth_failure':
        return '#F44336'; // Red
      default:
        return '#2196F3'; // Blue
    }
  };

  // Logged out state
  if (isLoggedOut) {
    return (
      <div className="qr-page" style={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar onLogout={handleLogout} />
        <div className="qr-container-full" style={{ 
          marginLeft: '260px', 
          width: 'calc(100% - 260px)',
          transition: 'all 0.3s ease',
          flex: 1,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px'
        }}>
          <div className="logged-out-container">
            <div className="logged-out-icon">📱</div>
            <h1>WhatsApp Disconnected</h1>
            <p className="logged-out-message">
              Your WhatsApp session has been disconnected. Click the button below to login again with a new QR code.
            </p>
            <button 
              className="whatsapp-login-btn"
              onClick={handleRequestNewQR}
            >
              🔐 WhatsApp Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-page" style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar onLogout={handleLogout} />
      <div className="qr-container-full" style={{ 
        marginLeft: '260px', 
        width: 'calc(100% - 260px)',
        transition: 'all 0.3s ease',
        flex: 1,
        minHeight: '100vh',
        padding: '40px'
      }}>
        <div className="qr-header">
          <div className="qr-header-top">
            <h1>📱 Scan WhatsApp QR Code</h1>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                className="add-new-number-btn"
                onClick={handleAddNewNumber}
                disabled={isAddingNewAccount}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                {isAddingNewAccount ? '⏳ Adding...' : '➕ Add New Number'}
              </button>
              {status.status === 'ready' && (
                <button
                  className="disconnect-btn"
                  onClick={handleDisconnectWhatsApp}
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? 'Disconnecting...' : '🚪 Disconnect WhatsApp'}
                </button>
              )}
            </div>
          </div>
          <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
            <span className="status-dot"></span>
            <span className="status-text">{status.message}</span>
          </div>
        </div>

        {/* Accounts List */}
        {accounts.length > 0 && (
          <div className="accounts-list-container" style={{
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginBottom: '15px', color: '#1e293b' }}>📋 Logged In Accounts</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {accounts.map((account) => (
                <div
                  key={account.accountId}
                  style={{
                    padding: '15px',
                    background: 'white',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    gap: '15px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                    <span style={{ fontSize: '1.5rem' }}>📱</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>
                        {account.phoneNumber || account.accountId}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        {account.isReady ? '✅ Connected' : '⏳ Connecting...'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {account.isActive ? (
                      <span style={{
                        padding: '6px 16px',
                        background: '#25D366',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        ✓ Active
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSetActive(account.accountId)}
                        style={{
                          padding: '8px 16px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        Set Active
                      </button>
                    )}
                    <button
                      onClick={() => handleLogoutAccount(account.accountId)}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                      }}
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {status.status === 'ready' ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>🎉 Connected Successfully!</h2>
            <p style={{ fontSize: '1.2rem', color: '#25D366', fontWeight: 'bold', margin: '20px 0' }}>
              Your WhatsApp is now connected and ready to send messages!
            </p>
            <p>You can now close this window and go back to the main page.</p>
            <button 
              onClick={() => window.close()} 
              className="close-button"
              style={{ marginTop: '20px' }}
            >
              ✅ Close Window
            </button>
          </div>
        ) : qrCode ? (
          <div className="qr-content">
            <h2>Scan QR Code with WhatsApp</h2>
            <div className="qr-code-wrapper-large">
              <QRCodeSVG value={qrCode} size={400} level="H" />
            </div>
            <div className="qr-instructions-box">
              <h3>How to Scan:</h3>
              <ol>
                <li>Open WhatsApp on your phone</li>
                <li>Go to <strong>Settings</strong> → <strong>Linked Devices</strong></li>
                <li>Tap <strong>"Link a Device"</strong></li>
                <li>Point your phone camera at this QR code</li>
              </ol>
            </div>
          </div>
        ) : (
          <div className="loading-message">
            <div className="loader"></div>
            <p>Generating QR code...</p>
            <button 
              onClick={handleRegenerateQR} 
              className="regenerate-button"
              style={{ marginTop: '20px' }}
            >
              🔄 Regenerate QR Code
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default QRCodePage;

